// src/pages/MyIssuesPage.tsx — SCE Issues
// Mes déclarations : filtre automatique sur le déclarant courant

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { Header } from '@/components/layout/Header'
import { ListeProblemesTelephone } from '@/components/issues/ListeProblemesTelephone'
import { IssueRow, IssueRowHeader } from '@/components/issues/IssueRow'
import { ListeDense, SqueletteDense } from '@/components/ui/ListeDense'
import { useEstBureau } from '@/hooks/useMediaQuery'
import { useIssues } from '@/hooks/useIssues'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { identitesDeclarant } from '@/utils/identiteDeclarant'
import type { IssueFilters } from '@/types/issue.types'
import { BoutonRafraichirProblemes } from '@/components/ui/BoutonRafraichir'
import { SuiteListe } from '@/components/layout/SuiteProblemes'

export function MyIssuesPage(): JSX.Element {
  const navigate = useNavigate()
  const estBureau = useEstBureau()
  const { user } = useCurrentUser()

  // Les deux identités, pas le seul UPN : pour un guest B2B, Author porte son
  // e-mail externe (cf. utils/identiteDeclarant). Sur l'UPN seul, cette page
  // restait désespérément vide pour un sous-traitant.
  const filters = useMemo<IssueFilters | undefined>(
    () => (user ? { declarant: identitesDeclarant(user) } : undefined),
    [user],
  )

  const { issues, loading, error } = useIssues(filters)

  const header = (
    <Header
      level={2}
      whiteOnLevel2
      title="Mes déclarations"
      showBack
      onBack={() => navigate('/')}
      rightAction={<BoutonRafraichirProblemes />}
    />
  )

  return (
    <AppShell header={header}>
      <section className="px-4 py-4">
        {!user && (
          <div
            className="p-4 rounded text-center"
            style={{
              backgroundColor: 'var(--color-white)',
              color: 'var(--color-text-secondary)',
            }}
          >
            Connexion requise pour voir vos déclarations.
          </div>
        )}

        {error && (
          <div
            className="p-3 rounded mb-3"
            style={{
              backgroundColor: 'var(--color-white)',
              color: 'var(--color-error)',
              fontSize: 'var(--font-size-copy2)',
            }}
          >
            {error}
          </div>
        )}

        {user && loading && issues.length === 0 && (
          estBureau ? (
            <SqueletteDense lignes={5} />
          ) : (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg animate-pulse"
                  style={{ height: 64, backgroundColor: 'var(--color-gray-10)' }}
                />
              ))}
            </div>
          )
        )}

        {user && !loading && issues.length === 0 && !error && (
          <div
            className="text-center py-10 rounded"
            style={{
              backgroundColor: 'var(--color-white)',
              color: 'var(--color-text-secondary)',
            }}
          >
            Vous n'avez déclaré aucun problème pour le moment.
          </div>
        )}

        {/* Même idiome que la liste générale : rangées au-delà de 768px,
            cartes en dessous. */}
        {issues.length > 0 &&
          (estBureau ? (
            <SuiteListe libelle="Mes déclarations" issues={issues}>
              <ListeDense>
                <IssueRowHeader />
                {issues.map((issue) => (
                  <IssueRow key={issue.id} issue={issue} />
                ))}
              </ListeDense>
            </SuiteListe>
          ) : (
            <ListeProblemesTelephone issues={issues} />
          ))}
      </section>
    </AppShell>
  )
}
